import create, { GetState, SetState } from 'zustand';
import { combine } from 'zustand/middleware';
import {
  Modify,
  WWWCompanyEditSelectFields,
  WWWCompanyEditDocument,
  WWWCompanyEditPayload,
} from '../../common/documents/WWWCompanyEditDocument';
import { countSelectedFromIDs } from '../common-react/selectOptionLogic';
import { WWWCompanyDocument } from '../common/documents/WWWCompanyDocument';
import { compareCompanyDocuments, convertCompanyDocumentToEditable } from '../components/company/companyEditCommon';
import { SelectedCountPair } from './advancedSearchStore';
import { staticState, StaticState } from './staticStore';

/** WWWCompanyEditDocument but select fields have been changed to SelectedCountPair */
export type CompanyEditState = Modify<WWWCompanyEditDocument, Record<keyof WWWCompanyEditSelectFields, SelectedCountPair>>;

export const companyEditStateDefaults: CompanyEditState = {
  description: '',
  phone: '',
  fax: '',
  email: '',
  logo: { path: '' },
  homepage: '',
  numberOfEmployees: { min: null, max: null },
  postalAddress: {
    street1: '',
    street2: '',
    city: '',
    postalCode: '',
  },
  visitingAddress: {
    street1: '',
    street2: '',
    city: '',
    postalCode: '',
  },
  domicile: {
    cityID: 0,
    cityName: '',
    provinceID: 0,
    provinceName: '',
    countryID: 0,
    countryName: '',
  },
  projectTypes: { selected: [], count: 0 },
  contractingForms: { selected: [], count: 0 },
  customerTargetGroups: { selected: [], count: 0 },
  machineryTypes: { selected: [], count: 0 },
  taitoCompetences: { selected: [], count: 0 },
  languages: { selected: [], count: 0 },
  talo80: { selected: [], count: 0 },
  operationalAreas: { selected: [], count: 0 },
  contactPersons: [],
  locations: [],
  references: [],
};

export type StateAction<T = {}, D = {}> = (
  set: SetState<CompanyEditState>,
  get: GetState<CompanyEditState>
) => (data?: T) => Promise<{ status: string; error?: string | JSX.Element } & D>;

/** Convert the original company document & the current edit state to the payload of the edit API endpoint */
export const doGetEditRequestPayload: StateAction<{ company: WWWCompanyDocument }, { payload?: WWWCompanyEditPayload }> =
  (set, get) =>
  async ({ company }) => {
    const { set, doGetEditRequestPayload, ...editState } = get() as any;

    let companyEditPayload: Pick<WWWCompanyEditPayload, 'companyID'> = {
      companyID: company.companyID,
    };

    const originalDocument = convertCompanyDocumentToEditable(company);
    const changedFields = compareCompanyDocuments(convertStateToEditable(editState), originalDocument);

    if (!changedFields) {
      return { status: 'nothing changed' };
    }

    return {
      status: 'ok',
      payload: {
        ...companyEditPayload,
        ...changedFields,
      },
    };
  };

/** Used to store the entire edited company document */
export const companyEditState = create(
  combine(companyEditStateDefaults, (set, get) => ({
    doGetEditRequestPayload: doGetEditRequestPayload(set, get),
    set,
  }))
);

const convertEditableSelectToState = (value: WWWCompanyEditSelectFields[keyof WWWCompanyEditSelectFields], path: keyof StaticState) => {
  const items = staticState.getState()[path];
  const selectedIDs = (value as any).map((entry) => entry.id);
  return {
    selected: selectedIDs,
    count: countSelectedFromIDs(selectedIDs, items),
  };
};

const convertStateSelectToEditable = (value: CompanyEditState[keyof WWWCompanyEditSelectFields], path: keyof StaticState) => {
  const selectedIDs = value.selected;

  if (path === 'machineryTypes') {
    return selectedIDs.map((id): WWWCompanyEditSelectFields['machineryTypes'][number] => ({
      id: parseInt(id),
      count: 1, // TODO: Implement
      range: null, // TODO: Implement
    }));
  }

  if (path === 'taitoCompetences') {
    return selectedIDs.map((id): WWWCompanyEditSelectFields['taitoCompetences'][number] => ({
      id: parseInt(id),
      totalAmountRange: '1-10', // TODO: Implement
    }));
  }

  return selectedIDs.map((id) => ({
    id: parseInt(id),
  }));
};

/** Convert WWWCompanyEditDocument to local edit state */
export const convertEditableToState = (document: WWWCompanyEditDocument): CompanyEditState => {
  const selectFields: Record<keyof WWWCompanyEditSelectFields, SelectedCountPair> = {
    projectTypes: convertEditableSelectToState(document.projectTypes, 'projectTypes'),
    contractingForms: convertEditableSelectToState(document.contractingForms, 'contractingForms'),
    customerTargetGroups: convertEditableSelectToState(document.customerTargetGroups, 'targetGroups'),
    machineryTypes: convertEditableSelectToState(document.machineryTypes, 'machineryTypes'),
    taitoCompetences: convertEditableSelectToState(document.taitoCompetences, 'taitoCompetences'),
    languages: convertEditableSelectToState(document.languages, 'languages'),
    talo80: convertEditableSelectToState(document.talo80, 'talo80'),
    operationalAreas: convertEditableSelectToState(document.operationalAreas, 'operationalAreas'),
  };

  const contactPersons = [...document.contactPersons.map((item) => ({ ...item }))];
  const locations = [...document.locations.map((item) => ({ ...item }))];
  const references = [...document.references.map((item) => ({ ...item }))];

  return {
    ...document,
    ...selectFields,
    contactPersons,
    locations,
    references,
  };
};

/** Convert local edit state to WWWCompanyEditDocument */
export const convertStateToEditable = (document: CompanyEditState): WWWCompanyEditDocument => {
  const selectFields: WWWCompanyEditSelectFields = {
    projectTypes: convertStateSelectToEditable(document.projectTypes, 'projectTypes'),
    contractingForms: convertStateSelectToEditable(document.contractingForms, 'contractingForms'),
    customerTargetGroups: convertStateSelectToEditable(document.customerTargetGroups, 'targetGroups'),
    machineryTypes: convertStateSelectToEditable(document.machineryTypes, 'machineryTypes') as any,
    taitoCompetences: convertStateSelectToEditable(document.taitoCompetences, 'taitoCompetences') as any,
    languages: convertStateSelectToEditable(document.languages, 'languages'),
    talo80: convertStateSelectToEditable(document.talo80, 'talo80'),
    operationalAreas: convertStateSelectToEditable(document.operationalAreas, 'operationalAreas'),
  };

  const contactPersons = [...document.contactPersons.map((item) => ({ ...item }))];
  const locations = [...document.locations.map((item) => ({ ...item }))];
  const references = [...document.references.map((item) => ({ ...item }))];

  return {
    ...document,
    ...selectFields,
    contactPersons,
    locations,
    references,
  };
};
